import { capitalize, startCase } from 'lodash';
import { ReactNode } from 'react';

import { INQUIRY_STATUS_LABEL_MAP } from 'dashboard/lib/constants/inquiry';
import { JsonLogicValue } from 'dashboard/lib/workflows';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  AdverseMediaType,
  adverseMediaTypeDisplayText,
  MatchLevel,
  matchLevelDisplayText,
} from 'dashboard/models/report/adverse-media';
import {
  pepClassDisplayText,
  PoliticallyExposedPersonClass,
} from 'dashboard/models/report/politically-exposed-person';
import { translateWatchlistType, WatchlistType } from 'dashboard/models/report/watchlist';
import { WorkflowFieldKey } from 'dashboard/models/workflow';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { COUNTRIES, US_STATES } from 'lib/constants/locales';

export type DisplayValueMapper = (value: string | string[]) => ReactNode;

export interface FieldMeta {
  name: string;
  description: string;
  displayValueMapper: DisplayValueMapper;
}

const displayRegularValue = (value: string): ReactNode => {
  // toString() formats Arrays with a `,` without a trailing space -- but we need trailing spaces
  // for the browser's break-word to work.
  return <>{value.toString().replace(/,/g, ', ')}</>;
};

const displayMultiValue = (values: string[]): ReactNode => {
  const concatenatedValue = values.reduce(
    (valueSoFar: string, currentValue: string, currentIndex: number) => {
      if (currentIndex !== 0) {
        valueSoFar += ', ';
      }
      valueSoFar += currentValue;
      return valueSoFar;
    },
    '',
  );

  return <>{concatenatedValue}</>;
};

const displayConditionalMultiValue = (value: string | string[]) =>
  Array.isArray(value) ? displayMultiValue([...value].sort()) : displayRegularValue(value);

const fieldMetas: Record<WorkflowFieldKey, FieldMeta> = {
  // Template, CaseTemplate
  [WorkflowFieldKey.Token]: {
    name: 'ID',
    description: "The object's ID",
    displayValueMapper: displayConditionalMultiValue,
  },
  // Inquiry, Cases, InquiryTemplateVersion, GraphQuery
  [WorkflowFieldKey.Id]: {
    name: 'ID',
    description: "The object's ID",
    displayValueMapper: displayConditionalMultiValue,
  },
  // Inquiry
  [WorkflowFieldKey.AddressRiskScore]: {
    name: 'Address risk score',
    description: 'The Coinbase risk score of the given address',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Age]: {
    name: 'Age',
    description: 'Age of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BehaviorThreatLevel]: {
    name: 'Behavior threat level',
    description: "Threat rating of the individual's behavior.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BrowserCount]: {
    name: 'Browser count',
    description: 'Number of unique browsers associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BrowserFingerprints]: {
    name: 'Browser fingerprints',
    description: 'Browser fingerprints associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BrowserResolvedUrl]: {
    name: 'Browser resolved url',
    description: 'The URL a browser navigates to',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BrowserTypes]: {
    name: 'Browser types',
    description: 'Browser types used by the individual during the inquiry.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => startCase(v)))
        : displayRegularValue(startCase(value as string)),
  },
  [WorkflowFieldKey.CaptureMethods]: {
    name: 'Capture method',
    description: 'Matches on the capture methods used by the individual during the inquiry.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => capitalize(v)))
        : displayRegularValue(capitalize(value as string)),
  },
  [WorkflowFieldKey.CheckReasons]: {
    name: 'Verification check reasons',
    description: 'Verification check reasons',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Checks]: {
    name: 'Checks',
    description: 'The checks of a verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ClientMobileSdkVersion]: {
    name: 'Client Mobile SDK Version',
    description: 'The version of the client mobile SDK.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ClientOsName]: {
    name: 'Client OS Name',
    description: 'The name of the client OS.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Country]: {
    name: 'Country',
    description:
      'The country of residence of the individual, as determined by the information provided during the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CountryCode]: {
    name: 'Country code',
    description: 'The two letter code for the country of residence of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CountryComparison]: {
    name: 'Country comparison',
    description:
      "Whether the device is in the user's country of residence, as determined by the information provided during the inquiry.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CountryFromSession]: {
    name: "Country from device's session",
    description: "The country the individual's device is located in.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Creator]: {
    name: 'Creator',
    description:
      'Either the email address of the user who created the inquiry, or "API" for programmatically created inquiries',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DatabaseTinVerificationTinType]: {
    name: 'TIN type',
    description: 'TIN type of TIN database verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DatabaseVerificationAddressMatch]: {
    name: 'Database verification address match',
    description: 'Match level of the address field in the database verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DatabaseVerificationIdentityMatchFailed]: {
    name: 'Database verification identity match failed',
    description: 'Failed fields in database identity match verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DatabaseVerificationSsnEditDistance]: {
    name: 'Database verification SSN edit distance',
    description: 'Edit distance of SSN from the identity comparison check.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DeviceCount]: {
    name: 'Device count',
    description: 'Number of unique devices associated with the object.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DeviceFingerprints]: {
    name: 'Device fingerprints',
    description: 'Device fingerprints associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DeviceNames]: {
    name: 'Device names',
    description: 'Device names associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DisplayPhoto]: {
    name: 'Display photo',
    description: 'The display photo',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DistractionEvents]: {
    name: 'Distraction events',
    description: 'Number of times the user left the flow.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DocumentCheckScore]: {
    name: 'Government ID confidence score',
    description: 'Score representing confidence in the validity of the submitted ID.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DocumentExtractions]: {
    name: 'Document extractions',
    description: 'Extractions off Documents uploaded during the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DocumentSimilarityScore]: {
    name: 'Selfie similarity score',
    description:
      'Score representing similarity between the submitted selfie and the face on the ID.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DocumentTypes]: {
    name: 'Document types',
    description: 'Types of documents uploaded in the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddress]: {
    name: 'Email address',
    description: 'Email address of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedAamvaVerificationCount]: {
    name: 'Failed AAMVA Verification count',
    description: 'Number of failed AAMVA verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedChecks]: {
    name: 'Failed verification checks',
    description: 'Failed checks.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CheckFailureReasons]: {
    name: 'Failed verification check reasons',
    description: 'Failed verification check reasons.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedChecksFromLatestVerifications]: {
    name: 'Failed checks from latest verifications',
    description: "Checks the inquiry's latest verifications have failed.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedDatabaseVerificationCount]: {
    name: 'Failed database verification count',
    description: 'Number of failed database verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedDatabaseBusinessVerificationCount]: {
    name: 'Failed business registry verification count',
    description: 'Number of failed business registry verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedDatabaseEcbsvVerificationCount]: {
    name: 'Failed database eCBSV verification count',
    description: 'Number of failed database verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedDocumentVerificationCount]: {
    name: 'Failed document verification count',
    description: 'Number of failed document verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedElectronicReplicaReasons]: {
    name: 'Failed electronic replica checks',
    description: 'Reasons the inquiry has failed for Electronic Replica Detection.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedGovernmentIdVerificationCount]: {
    name: 'Failed government ID verification count',
    description: 'Number of failed government ID verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedSelfieVerificationCount]: {
    name: 'Failed selfie verification count',
    description: 'Number of failed selfie verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedVerificationAttempts]: {
    name: 'Number of failed verification attempts',
    description: 'Within the inquiry, the number of failed verification attempts.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Fields]: {
    name: 'Fields',
    description: 'Value of a field on the object.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FilledProperties]: {
    name: 'Filled properties',
    description: 'Properties that were filled on this inquiry. Eg. name-first, name-last, etc',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FlowState]: {
    name: 'Flow State',
    description: 'Internal state used in the Inquiry Flow',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Geolocations]: {
    name: 'Geolocations',
    description: 'The geolocations associated with the object.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GeolocationToResidencyDelta]: {
    name: 'Geolocation to residency delta',
    description:
      "Distance in meters from the individual's residence to the device's location. Warning: proxy usage will result in wider distances.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdDesignations]: {
    name: 'Government ID designations',
    description: 'Designated properties about a government ID.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdExpirationDate]: {
    name: 'Government ID expiration date',
    description: 'The date the government ID expires.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdExpirationDelta]: {
    name: 'Government ID expiration delta',
    description: "The number of days from the current date to the id's expiration date",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdExpired]: {
    name: 'Government ID expired',
    description: 'Whether or not an expired ID was submitted.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdInquiryComparisonMatchFailed]: {
    name: 'Government ID inquiry comparison failed',
    description: 'Failed fields in Government ID inquiry comparison check.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdTags]: {
    name: 'Government ID tags',
    description: 'Deprecated: Use Government ID designations instead',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdVehicleClasses]: {
    name: 'Government ID vehicle classes',
    description: 'Vehicle classes associated with the government ID.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdVisaStatus]: {
    name: 'Government ID visa status',
    description: "The status of the government ID's visa if applicable.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HasDocument]: {
    name: 'Has documents',
    description: 'Whether or not the inquiry has supplementary documents submitted.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HasGovernmentIdExtraction]: {
    name: 'Has government ID extraction',
    description: 'Whether or not the inquiry has a government ID that supports extraction.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HasSelfie]: {
    name: 'Has selfies',
    description: 'Whether or not the object has selfies.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HasStarted]: {
    name: 'Has started',
    description: 'Whether or not the inquiry has been started.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HasUnknownId]: {
    name: 'Has unknown ID',
    description: 'Whether or not the inquiry has an ID with an unknown type.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IncognitoBrowsingDetected]: {
    name: 'Incognito browsing detected',
    description: 'Whether the individual used private browsing.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdClasses]: {
    name: 'Government ID type',
    description:
      'The type of government ID submitted, for example, driver license, as collected from information provided during the inquiry.',
    displayValueMapper: (value: string | string[]) =>
      Array.isArray(value)
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          displayMultiValue(value.map((v) => GOVERNMENT_ID_DISPLAY_STRINGS[v as GovernmentIdClass]))
        : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          GOVERNMENT_ID_DISPLAY_STRINGS[value as GovernmentIdClass],
  },
  [WorkflowFieldKey.IpAddresses]: {
    name: 'IP Addresses',
    description: 'The ip addresses',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IpCount]: {
    name: 'IP address count',
    description: 'Number of unique IP addresses associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IpIspList]: {
    name: 'IP ISP list',
    description: 'List of ISP names for the IP addresses associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IpServiceType]: {
    name: 'IP Service Type',
    description: 'List of service types for the IP addresses associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LinkUrl]: {
    name: 'Inquiry link',
    description: 'URL for a hosted flow to verify identity.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ListMatches]: {
    name: 'List matches',
    description: 'Lists the individual has matched against.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameFirst]: {
    name: 'Name first',
    description: 'First name of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchedDataSourceTypes]: {
    name: 'Matched data source types',
    description: 'Data source types the individual has matched against.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameLast]: {
    name: 'Name last',
    description: 'Last name of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LastTwoVerificationsGeolocationDelta]: {
    name: 'Last two verifications geolocation delta',
    description:
      'Distance in meters between the locations of the last two verifications during the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LatestSessionDevice]: {
    name: 'Device group associated with latest session',
    description: 'Device group of device used by the individual in the most recent inquiry session',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LivenessCheckScore]: {
    name: 'Selfie liveness score',
    description: 'Score representing confidence in the liveness of the submitted selfie.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NetworkOrganizations]: {
    name: 'Network Organizations',
    description: 'The organizations that are operating the networks used on the Inquiry',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PassedDatabaseVerificationCount]: {
    name: 'Passed database verification count',
    description: 'Number of passed database verifications on the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PassedDatabaseEcbsvVerificationCount]: {
    name: 'Passed database eCBSV verification count',
    description: 'Number of passed database verifications on the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PassedGovernmentIdVerificationCount]: {
    name: 'Passed government ID verification count',
    description: 'Number of passed government ID verifications on the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PassedSelfieVerificationCount]: {
    name: 'Passed selfie verification count',
    description: 'Number of passed selfie verifications on the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PhoneNumber]: {
    name: 'Phone number',
    description: 'Phone number of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ProxyDetected]: {
    name: 'Proxy detected',
    description: 'Whether the individual used a proxy or VPN while completing the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SelectedCountry]: {
    name: 'Selected country',
    description: 'The country of residence of the individual, as reported by the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SelfieLivenessRiskLevel]: {
    name: 'Selfie liveness risk level',
    description: 'Risk level of liveness of the submitted selfie.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SelfiePhoto]: {
    name: 'Selfie Photo',
    description: 'The primary selfie photo',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Selfies]: {
    name: 'Selfies',
    description: 'The selfies ',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SessionCount]: {
    name: 'Session count',
    description: 'Number of unique sessions associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SessionsGeolocationDelta]: {
    name: 'Sessions geolocation delta',
    description: 'The max distance between any two sessions associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SessionsIpLocationDelta]: {
    name: 'Sessions ip location delta',
    description:
      'The max distance by ip lookup between any two sessions associated with the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SsnDigitCount]: {
    name: 'Social security number digit count',
    description: 'The number of digits in the social security number collected from the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Tags]: {
    name: 'Tags',
    description: 'Tags associated with the object.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.TorDetected]: {
    name: 'Tor detected',
    description: 'Whether the individual used Tor while completing the inquiry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ThreatLevels]: {
    name: 'Network threat levels',
    description: "Threat rating of the individual's networks.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.TinType]: {
    name: 'TIN type',
    description: 'TIN type of TIN database verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.UsState]: {
    name: 'US State',
    description:
      'The state of residence of an individual in the US, as determined by the information provided during the inquiry.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => US_STATES[v as keyof typeof US_STATES]))
        : US_STATES[value as keyof typeof US_STATES],
  },
  [WorkflowFieldKey.VehicleClasses]: {
    name: 'Vehicle classes',
    description: 'Vehicle classes associated with the verification.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AccountStatus]: {
    name: 'Account status matches',
    description: 'The current status of the account.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AnyPriorInquiryStatus]: {
    name: 'Any prior inquiry status',
    description: 'Inquiry status values on prior inquiries.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => INQUIRY_STATUS_LABEL_MAP[v as InquiryStatus]))
        : INQUIRY_STATUS_LABEL_MAP[value as InquiryStatus],
  },
  [WorkflowFieldKey.RedirectUri]: {
    name: 'Redirect URI',
    description:
      'URL a hosted flow inquiry will redirect to upon completion. This parameter is set at inquiry creation.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ReferenceId]: {
    name: 'Reference ID',
    description: 'An external ID associated with the object.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SimilarAccountsBySelfie]: {
    name: 'Accounts with similar selfies',
    description: 'Number of accounts with similar selfie photos.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.TrustedDevices]: {
    name: 'Trusted devices',
    description: 'Devices trusted by this account.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RiskLabel]: {
    name: 'Risk label',
    description: "The inquiry's risk label",
    displayValueMapper: displayConditionalMultiValue,
  },

  // graph
  [WorkflowFieldKey.GraphQueryAccountStatuses]: {
    name: 'Account statuses',
    description: 'List of Account Statuses from all Accounts Returned in Graph Query.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GraphQueryAccountTags]: {
    name: 'Account tags',
    description: 'List of Account Tags from all Accounts Returned in Graph Query.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GraphQueryAccountTokens]: {
    name: 'Account tokens',
    description: 'List of Account Tokens Returned in Graph Query.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GraphQueryInquiryStatuses]: {
    name: 'Inquiry statuses',
    description: "List of Inquiry Statuses from all returned Accounts' Inquiries.",
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GraphQueryNumberOfAccountNodes]: {
    name: 'Number of account nodes',
    description: 'Total Number of Accounts Returned (includes Account in trigger if applicable)',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/address-lookup
  [WorkflowFieldKey.CarrierRouteType]: {
    name: 'Carrier route type',
    description: 'Carrier route type associated with this address.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => capitalize(v)))
        : displayRegularValue(capitalize(value as string)),
  },
  [WorkflowFieldKey.CommercialMailReceivingAgency]: {
    name: 'Commercial mail receiving agency',
    description:
      'Whether or not the address belongs to a commercial mail receiving agency (CMRA) or a private mail box (PMB).',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PoBox]: {
    name: 'PO box',
    description: 'Whether or the office belongs to a Post Office (PO) Box',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResidentialDeliveryIndicator]: {
    name: 'Residential delivery indicator',
    description: 'What the address is classified as. Either "Commercial" or "Residential"',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ZipType]: {
    name: 'Zip type',
    description: 'The type of the zip code.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/adverse-media and
  // report/business-adverse-media

  // IsContinuousMatch is also used for report/watchlist and report/business-watchlist
  [WorkflowFieldKey.IsContinuousMatch]: {
    name: 'Continuous run',
    description: 'Whether or not this was a continuous run of a report.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AdverseMediaTypes]: {
    name: 'Adverse media types',
    description: 'Adverse media types on this report.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => adverseMediaTypeDisplayText(v as AdverseMediaType)))
        : adverseMediaTypeDisplayText(value as AdverseMediaType),
  },
  [WorkflowFieldKey.RelevanceTypes]: {
    name: 'Adverse media relevance types',
    description: 'Relevance types on this report.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => matchLevelDisplayText(v as MatchLevel)))
        : matchLevelDisplayText(value as MatchLevel),
  },
  [WorkflowFieldKey.ReportCountries]: {
    name: 'Countries',
    description: 'Countries recorded on this report.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => COUNTRIES[v as keyof typeof COUNTRIES]))
        : COUNTRIES[value as keyof typeof COUNTRIES],
  },

  // report/business-classification
  [WorkflowFieldKey.IsHighRisk]: {
    name: 'Is High Risk',
    description: 'Whether the business is in a high risk industry.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Keywords]: {
    name: 'Keywords',
    description: 'Keywords about the business.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MerchantCategoryCodes]: {
    name: 'Merchant Category Codes',
    description: 'Merchant category codes for this business.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NaicsCodes]: {
    name: 'NAICS Codes',
    description: 'North American Industry Classification System codes for this business.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/business-lookup
  [WorkflowFieldKey.MatchStatusAddress]: {
    name: 'Address Match',
    description: 'Whether there was a match on the overall address',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchStatusAssociatedPerson]: {
    name: 'Associated Person Match',
    description: 'Whether there was a match on associated person',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchStatusBusinessName]: {
    name: 'Business Name Match',
    description: 'Whether there was a match on business name',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchStatusEin]: {
    name: 'EIN Match',
    description: 'Whether there was a match on EIN',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchStatusStrength]: {
    name: 'Match strength',
    description: 'Whether there was a match on the report',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.QueryAddressTypes]: {
    name: 'Query Address Types',
    description:
      'The type of the address that was queried (e.g. registered agent, site, mailing, etc.)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.QueryAddressVacancyStatus]: {
    name: 'Query Address Vacancy Status',
    description: 'If the input address into the report is vacant or not.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/business-online-presence
  [WorkflowFieldKey.Homepage]: {
    name: 'Homepage',
    description: "A business's homepage",
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/business-social-media
  [WorkflowFieldKey.LinkedinUrl]: {
    name: 'LinkedIn URL',
    description: 'The LinkedIn URL',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LinkedinId]: {
    name: 'LinkedIn ID',
    description: 'The LinkedIn ID',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FacebookUrl]: {
    name: 'Facebook URL',
    description: 'The Facebook URL',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.TwitterUrl]: {
    name: 'Twitter URL',
    description: 'The Twitter URL',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/integration/chainalysis-address-screening
  [WorkflowFieldKey.RiskLevel]: {
    name: 'Risk Level',
    description: 'The risk level associated with a crypto address',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/email-address
  [WorkflowFieldKey.EmailAddressDomain]: {
    name: 'Email Address Domain',
    description: 'The email address domain',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressDomainCreatedDays]: {
    name: 'Email Address Domain Created',
    description: 'The number of days since the email domain was created.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressDomainExists]: {
    name: 'Email Address Domain Exists',
    description: 'Whether or not the email address domain exists.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressDomainReputations]: {
    name: 'Email Address Domain Reputation',
    description: 'Reputation of an email address domain. (Deprecated)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressFirstSeenDays]: {
    name: 'Email Address First Seen',
    description: 'The number of days since the email address was first seen.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressEstimatedAgeDays]: {
    name: 'Email Address Estimated Age',
    description: 'The estimated number of days since the email address was created.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsBlacklisted]: {
    name: 'Email Address Blacklisted',
    description: 'Whether or not the email address is blacklisted. (Deprecated)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsDeliverable]: {
    name: 'Email Address Deliverable',
    description: 'Whether or not the email address is deliverable.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsDisposable]: {
    name: 'Email Address Disposable',
    description: 'Whether or not the email address is disposable.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsFreeProvider]: {
    name: 'Email Address Free Provider',
    description: 'Whether or not the email address is a free provider. (Deprecated)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsSpam]: {
    name: 'Email Address Spam',
    description: 'Whether or not the email address is spam.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsSuspicious]: {
    name: 'Email Address Suspicious',
    description: 'Whether or not the email address is suspicious.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressIsValidMx]: {
    name: 'Email Address Valid Mx',
    description: 'Whether or not the email address has a valid MX record.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressLastSeenDays]: {
    name: 'Email Address Last Seen',
    description: 'The number of days since the email address was last seen. (Deprecated)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressReferenceCount]: {
    name: 'Email Address Reference Count',
    description: 'The number of references the email address has. (Deprecated)',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressReputations]: {
    name: 'Email Address Reputation',
    description: 'Reputation of an email address.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressSMTPScore]: {
    name: 'Email SMTP Score',
    description: 'Score associated with the SMTP availability of the email address.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressSuspect]: {
    name: 'Suspect',
    description:
      'Whether the mail server is currently replying with a temporary mail server error or if the email verification system is unable to verify the email address due to a broken SMTP handshake.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.EmailAddressCatchAll]: {
    name: 'Catch All',
    description: 'Whether the the mail server verifies all emails tested against it as valid.',
    displayValueMapper: displayConditionalMultiValue,
  },

  [WorkflowFieldKey.EmailAddressTimedOut]: {
    name: 'Time out',
    description: 'Whether or not the request timed out in an attempt to validate the email',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/integration/equifax-oneview
  [WorkflowFieldKey.FicoScore8EquifaxDataNonFacta]: {
    name: 'FICO Score 8 with Equifax Data (Non-Facta)',
    description: 'Credit score calculated using FICO Score 8 with Equifax data. Non-Facta version',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FicoScore9EquifaxDataNonFacta]: {
    name: 'FICO Score 9 with Equifax Data (Non-Facta)',
    description: 'Credit score calculated using FICO Score 9 with Equifax data. Non-Facta version',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.VantageScore4]: {
    name: 'VantageScore 4.0',
    description: 'Credit score calculated using VantageScore 4.0',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/phone-number
  [WorkflowFieldKey.PhoneCarrier]: {
    name: 'Phone carrier',
    description: 'Inferred phone carrier according to phone records.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PhoneCountryCode]: {
    name: 'Phone country code',
    description: 'Inferred phone country-code according to phone records.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PhoneRiskRecommendations]: {
    name: 'Phone number risk recommendation',
    description: 'Risk recommendation of phone number based on properties of device and of use.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PhoneType]: {
    name: 'Phone type',
    description: 'Inferred type of phone according to phone records.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RiskScore]: {
    name: 'Phone number risk score',
    description: 'Risk score of phone number based on properties of device and of use.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/politically-exposed-person
  [WorkflowFieldKey.PoliticallyExposedPersonClassTypes]: {
    name: 'PEP class types',
    description: 'PEP classes on a politically exposed person report',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(
            value.map((v) => pepClassDisplayText(v as PoliticallyExposedPersonClass)),
          )
        : pepClassDisplayText(value as PoliticallyExposedPersonClass),
  },

  // report/integration/sentilink-application-risk
  [WorkflowFieldKey.SyntheticScore]: {
    name: 'Synthetic score',
    description:
      'Score representing the likelihood that an account is an instance of synthetic identity fraud.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/integration/sentilink-scores
  [WorkflowFieldKey.AbuseScore]: {
    name: 'Abuse score',
    description:
      'Score representing the likelihood that an account is an instance of synthetic identity fraud.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdTheftScore]: {
    name: 'ID theft score',
    description:
      'Score representing the likelihood that the account is a victim of identity theft.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FirstIntegrationType]: {
    name: 'First Integration Type',
    description: 'The integration type by which the inquiry was first created.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FirstPartySyntheticScore]: {
    name: 'First party synthetic score',
    description: 'Score representing The likelihood that the account is a first party synthetic.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ThirdPartySyntheticScore]: {
    name: 'Third party synthetic score',
    description: 'Score representing the likelihood that an account is a third party synthetic.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/integration/trm-wallet-screening
  [WorkflowFieldKey.AddressCategoryRiskScoreLevel]: {
    name: 'Address Category Risk Score',
    description: 'The highest address category risk score found',
    displayValueMapper: displayConditionalMultiValue,
  },

  // report/watchlist, report/business-watchlist and report/politically-exposed-person
  [WorkflowFieldKey.MatchLevels]: {
    name: 'Match Levels',
    description: 'The comparison result of all the matched entities',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchFound]: {
    name: 'Match found',
    description: 'Whether or not there was any match found on the report.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchTypes]: {
    name: 'Match types',
    description: 'Matches by watchlist types on this report.',
    displayValueMapper: (value: JsonLogicValue) =>
      Array.isArray(value)
        ? displayMultiValue(value.map((v) => translateWatchlistType(v as WatchlistType)))
        : translateWatchlistType(value as WatchlistType),
  },
  [WorkflowFieldKey.MatchTags]: {
    name: 'Match tags',
    description: 'Matches by watchlist tags on this report.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.HighestSimilarityLevel]: {
    name: 'Highest similarity level',
    description: 'Highest profile image similarity level',
    displayValueMapper: displayConditionalMultiValue,
  },
  // Cases
  [WorkflowFieldKey.CaseResolution]: {
    name: 'Case resolution',
    description: 'Resolution of this case.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CaseState]: {
    name: 'Case status',
    description: 'Status of this case.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // selfie file image
  [WorkflowFieldKey.RegistrationStatus]: {
    name: 'Selfie registry registration status',
    description: 'Status of selfie registry registration.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Data]: {
    name: 'Selfie photo',
    description: 'Selfie\u2019s center photo.',
    displayValueMapper: displayConditionalMultiValue,
  },

  // graph/status
  // verification/aamva
  // verififcation/database-business
  [WorkflowFieldKey.AddressCityMatchLevel]: {
    name: 'Address city match level',
    description: 'Best comparison result on address_city.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressPostalCodeMatchLevel]: {
    name: 'Address postal code match level',
    description: 'Best comparison result on address_postal_code.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressStreet1MatchLevel]: {
    name: 'Address street 1 match level',
    description: 'Best comparison result on address_street_1.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressSubdivisionMatchLevel]: {
    name: 'Address subdivision match level',
    description: 'Best comparison result on address_subdivision.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessNameMatchLevel]: {
    name: 'Business name match level',
    description: 'Best comparison result on business_name.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RegistrationNumberMatchLevel]: {
    name: 'Registration number match level',
    description: 'Best comparison result on registration_number.',
    displayValueMapper: displayConditionalMultiValue,
  },
  // verification/database
  // verification/government-id
  // verification/selfie
  [WorkflowFieldKey.Status]: {
    name: 'Status',
    description: 'Status of the given object.',
    displayValueMapper: displayConditionalMultiValue,
  },

  [WorkflowFieldKey.AgeThresholdComparisonRiskLevel]: {
    name: 'Age Threshold Comparison Risk Level',
    description:
      "Risk level that individual's age is outside of configured min/max age thresholds.",
    displayValueMapper: (value: JsonLogicValue) => displayRegularValue(value as string),
  },
  [WorkflowFieldKey.AgeThresholdComparisonRiskScore]: {
    name: 'Age Threshold Comparison Risk Score',
    description:
      "Risk score that individual's age is outside of configured min/max age thresholds.",
    displayValueMapper: (value: JsonLogicValue) => displayRegularValue(value as string),
  },
  [WorkflowFieldKey.Designations]: {
    name: 'Government ID designations',
    description: 'Designated properties about a government ID.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.UserRiskScore]: {
    name: 'User risk score',
    description: 'The Coinbase risk score of the given user',
    displayValueMapper: displayConditionalMultiValue,
  },

  [WorkflowFieldKey.Reasons]: {
    name: 'Reasons',
    description: 'The reasons for the returned Coinbase risk scores',
    displayValueMapper: displayConditionalMultiValue,
  },

  // InquiryTemplateVersion
  [WorkflowFieldKey.Name]: {
    name: 'Name',
    description: "The object's name",
    displayValueMapper: displayConditionalMultiValue,
  },

  // INCOMPLETE - to all relevant teams, please fill out descriptions
  [WorkflowFieldKey.AddressCity]: {
    name: 'Address city',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressCountryCode]: {
    name: 'Address country code',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressMatch]: {
    name: 'Address match',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressPostalCode]: {
    name: 'Address postal code',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressStreet1]: {
    name: 'Address street 1',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressStreet2]: {
    name: 'Address street 2',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.AddressSubdivision]: {
    name: 'Address subdivision',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Birthdate]: {
    name: 'Birthdate',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Birthplace]: {
    name: 'Birthplace',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessEntityType]: {
    name: 'Business entity type',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessFormationDate]: {
    name: 'Business formation date',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessFormationSubdivision]: {
    name: 'Business formation subdivision',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessIndustry]: {
    name: 'Business industry',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.BusinessName]: {
    name: 'Business name',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CaliforniaAttorneyGeneralStatus]: {
    name: 'California attorney general status',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyAnnualRevenue]: {
    name: 'Company annual revenue',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyCountryCode]: {
    name: 'Company country code',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyDescription]: {
    name: 'Company description',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyDomain]: {
    name: 'Company domain',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyEin]: {
    name: 'Company EIN',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyEmployeesRange]: {
    name: 'Company employees range',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyEstimatedAnnualRevenue]: {
    name: 'Company estimated annual revenue',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyIndustry]: {
    name: 'Company industry',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyLinkedin]: {
    name: 'Company LinkedIn',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyMarketCap]: {
    name: 'Company market cap',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyRaised]: {
    name: 'Company raised',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanySubindustry]: {
    name: 'Company subindustry',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CompanyTag]: {
    name: 'Company tags',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CorporateRegistrationStatus]: {
    name: 'Corporate registration statuses',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.CreatedAt]: {
    name: 'Created at',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DeceasedDetection]: {
    name: 'Deceased detection',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Deliverable]: {
    name: 'Deliverable',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DisallowedTypeDetection]: {
    name: 'Disallowed type detection',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.DocumentNumber]: {
    name: 'Document number',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ExpirationDate]: {
    name: 'Expiration date',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FailedMatchGroup]: {
    name: 'Failed match groups',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.FromEmailAddress]: {
    name: 'From email address',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.GovernmentIdUnprocessableSubmissionDetectionReason]: {
    name: 'Government ID unprocessable submission detection reasons',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdClass]: {
    name: 'ID class',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdentificationClass]: {
    name: 'Identification class',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdentificationNumber]: {
    name: 'Identification number',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IdentificationNumberMap]: {
    name: 'Identification number map',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.InvalidFormatDetection]: {
    name: 'Invalid format detection',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IrsPublication78Verified]: {
    name: 'IRS publication 78 verified',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IsOnCaliforniaFranchiseTaxBoardRevokedList]: {
    name: 'Is on California franchise tax board revoked list',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IsOnIrsBusinessMasterFile]: {
    name: 'Is on IRS business master file',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },

  [WorkflowFieldKey.IsReligiousOrganization]: {
    name: 'Is a religious organization',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IssueDate]: {
    name: 'Issue date',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IssuingCountry]: {
    name: 'Issuing country',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.IssuingSubdivision]: {
    name: 'Issuing subdivision',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Label]: {
    name: 'Label',
    description: 'Label on the transaction.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.LegalEntityType]: {
    name: 'Legal entity type',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchGroupName]: {
    name: 'Match group name',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchGroupPassedAttribute]: {
    name: 'Match group passed attributes',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.MatchStatus]: {
    name: 'Match status',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameBusiness]: {
    name: 'Name business',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameComparison]: {
    name: 'Name comparison',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameMatch]: {
    name: 'Name match',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NameMiddle]: {
    name: 'Name middle',
    description: 'Middle name of the individual.',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NativeNameFirst]: {
    name: 'First name of the individual in the primary language of the country if not in name_first',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NativeNameLast]: {
    name: 'Last name in the primary language of the country if not in name_last',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.NativeNameMiddle]: {
    name: 'Middle name in the primary language of the country if not in name_middle',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.PassedMatchGroup]: {
    name: 'Passed match groups',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RecordMatchStatus]: {
    name: 'Record match status',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RelatedCaseId]: {
    name: 'Related case ID',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RelatedTxnId]: {
    name: 'Related transaction ID',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ReportStatus]: {
    name: 'Report status',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.RequiredChecksFromLatestVerification]: {
    name: 'Required checks from latest verifications',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResolvedAddressCity]: {
    name: 'Resolved address city',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResolvedAddressPostalCode]: {
    name: 'Resolved address postal code',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResolvedAddressStreet1]: {
    name: 'Resolved address street 1',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResolvedAddressStreet2]: {
    name: 'Resolved address street 2',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ResolvedAddressSubdivision]: {
    name: 'Resolved address subdivision',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.ServiceAvailableDetection]: {
    name: 'Service available detection',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Sex]: {
    name: 'Sex',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.SocialSecurityNumber]: {
    name: 'Social security number',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.Tin]: {
    name: 'TIN',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.TinMatch]: {
    name: 'TIN match',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.UnsupportedCountry]: {
    name: 'Unsupported country',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.WatchlistMatch]: {
    name: 'Watchlist match',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.WebsiteMatchLevel]: {
    name: 'Website match level',
    description: '',
    displayValueMapper: displayConditionalMultiValue,
  },
  [WorkflowFieldKey.WorkflowApiKey]: {
    name: 'Workflow API key',
    description: 'The API key used by workflows to hit your Persona API.',
    displayValueMapper: displayConditionalMultiValue,
  },
};

export default fieldMetas;
